import { flatten } from 'lodash';
import {
  ChildModule,
  ChildQuestion,
  Condition,
  ConditionGroup,
  GetModules,
  GetQuestionnaire,
  Module,
  Question,
} from './models';
import { WcgMenuOption } from '@wcg/frontend-framework';
import { FormGroup } from '@angular/forms';
import { IrisWorkspaceType } from '@data-access/setup/types/iris-workspace.type';
import { duplicateFileError } from '@shared/constants';

export function isQuestion(
  target: Question | Module | null,
): target is Question {
  return target !== null && 'type' in target;
}

export function getConditionalQuestions(
  moduleId: string,
  target: Question | Module | ChildModule,
  questionnaire: GetQuestionnaire,
): Array<{ question: Question | ChildQuestion; enabled: boolean }> {
  const targetIsQuestion = isQuestion(target);
  const targetIsChildModule =
    !targetIsQuestion && 'questions' in target && target.parent_id;

  const moduleIndex = questionnaire.modules.findIndex(
    (module) => module.id === moduleId,
  );

  if (moduleIndex < 0) {
    console.error('Module not found');
    return [];
  }

  const module = questionnaire.modules[moduleIndex];

  const previousQuestions = flatten(
    questionnaire.modules.slice(0, moduleIndex).map(getAllModuleQuestions),
  );

  let nextQuestions: Array<{
    question: Question | ChildQuestion;
    enabled: boolean;
  }> = [];

  if (targetIsQuestion) {
    const subModuleId = (target as Question).module_id;
    const subModuleIndex =
      questionnaire.modules[moduleIndex].sub_modules?.findIndex(
        (subModule) => subModule.id === subModuleId,
      ) ?? -1;

    if (subModuleIndex === undefined || subModuleIndex < 0) {
      console.error('Submodule not found');
      return [];
    }

    const subModule = (module.sub_modules as ChildModule[])[subModuleIndex] ?? {
      sub_modules: [],
    };

    const previousSubModulesQuestions = flatten(
      (module.sub_modules || [])
        .slice(0, subModuleIndex)
        .map(getSubmoduleQuestions),
    );

    previousQuestions.push(...previousSubModulesQuestions);

    const allSubModuleQuestions = getSubmoduleQuestions(subModule);

    const questionIndex =
      allSubModuleQuestions?.findIndex((q) => q.id === target.id) ?? -1;

    previousQuestions.push(
      ...(allSubModuleQuestions?.slice(0, questionIndex) ?? []),
    );

    nextQuestions = (allSubModuleQuestions?.slice(questionIndex + 1) ?? []).map(
      (question) => ({
        enabled: false,
        question,
      }),
    );
  } else if (targetIsChildModule) {
    const subModuleIndex =
      module.sub_modules?.findIndex(
        (subModule) => subModule.id === (target as ChildModule).id,
      ) ?? -1;

    if (subModuleIndex === undefined || subModuleIndex < 0) {
      return [];
    }

    previousQuestions.push(
      ...flatten(
        module.sub_modules
          ?.slice(0, subModuleIndex)
          ?.map(getSubmoduleQuestions) ?? [],
      ),
    );

    nextQuestions =
      (
        flatten(
          (target as ChildModule).questions?.map((q) => [
            q,
            ...(q.children || []),
          ]) ?? [],
        ) as Array<Question | ChildQuestion>
      ).map((question) => ({
        enabled: false,
        question,
      })) ?? [];

    const nextSubmoduleQuestions =
      flatten(
        module.sub_modules
          ?.slice(subModuleIndex + 1)
          ?.map(getSubmoduleQuestions) ?? [],
      ).map((question: Question | ChildQuestion) => ({
        enabled: false,
        question,
      })) ?? [];

    nextQuestions.push(...nextSubmoduleQuestions);
  } else {
    nextQuestions = [
      ...flatten(module.sub_modules?.map(getSubmoduleQuestions) ?? []).map(
        (question) => ({
          enabled: false,
          question,
        }),
      ),
    ];
  }

  const allPreviousQuestions = previousQuestions.map((question) => ({
    enabled:
      question.type?.name !== 'text' && question.type?.name !== 'document',
    question,
  }));

  const nextModules = questionnaire.modules.slice(moduleIndex + 1);

  const nextModuleQuestions = nextModules
    .reduce(
      (acc, module) =>
        acc.concat(
          module.sub_modules?.flatMap(
            (subModule) =>
              flatten(
                subModule.questions?.map((q) => [q, ...(q.children || [])]),
              ) ?? [],
          ) ?? [],
        ),
      [] as Array<Question | ChildQuestion>,
    )
    .map((question) => ({
      enabled: false,
      question,
    }));

  return [
    ...allPreviousQuestions,
    ...(isQuestion(target)
      ? [
          {
            enabled: false,
            question: target,
          },
        ]
      : []),
    ...nextQuestions,
    ...nextModuleQuestions,
  ].filter(
    (q) =>
      !q.question.type?.data?.is_message &&
      q.question.type?.name !== 'add-more',
  );
}

export function createConditionGroups(
  question: Question | Module,
): ConditionGroup[] {
  const conditionGroups: { [num: number]: ConditionGroup } = {};

  if (!question.conditions) {
    return [];
  }

  question.conditions.forEach((condition) => {
    const groupNum = condition.group_num ?? 0;

    if (!conditionGroups[groupNum]) {
      conditionGroups[groupNum] = {
        conditions: [],
        number: condition.group_num ?? 0,
        operator: condition.condition_operator,
      };
    }

    conditionGroups[groupNum].conditions.push(condition);
  });

  return Object.values(conditionGroups);
}

export function getAllQuestionnaireQuestions(questionnaire: GetQuestionnaire) {
  if (!questionnaire || !Array.isArray(questionnaire.modules)) {
    return [];
  }
  return flatten(questionnaire.modules.map(getAllModuleQuestions));
}

export function getAllModuleQuestions(
  module: GetModules,
): Array<Question | ChildQuestion> {
  return flatten(module.sub_modules?.map(getSubmoduleQuestions));
}

export function getSubmoduleQuestions(
  subModule: ChildModule,
): Array<Question | ChildQuestion> {
  return flatten(
    subModule.questions?.map((q) => [q, ...(q.children || [])]) ?? [],
  );
}

export function conditionGroupToConditionList(
  conditionGroups: ConditionGroup[],
): Condition[] {
  return (
    conditionGroups.reduce(
      (acc, group) =>
        (acc ?? []).concat(
          group.conditions.map((condition) => ({
            ...condition,
            group_num: group.number,
            condition_operator: group.operator,
          })),
        ),
      [] as Condition[],
    ) ?? []
  );
}

export function getOriginType(origin: string): string {
  if (!origin) return '';
  return origin === 'erm' ? 'eRM' : 'non-eRM';
}

export function handleSubmissionTypeSelection(
  event: WcgMenuOption,
  workspaceTypes: IrisWorkspaceType[],
  questionnaireForm: FormGroup,
): { workspaceEntityTypes: { id: string; label: string }[] } {
  const selectedWorkspaceType = workspaceTypes.find(
    (entityType) => entityType.id === event.id,
  );

  if (!selectedWorkspaceType?.data?.entity?.length) {
    questionnaireForm
      .get('workspace_type_entity')
      ?.setValue('', { emitEvent: false });
    return { workspaceEntityTypes: [] };
  }

  const currentEntityType = questionnaireForm.get('workspace_type_entity')
    ?.value?.id;
  if (!selectedWorkspaceType?.data?.entity?.includes(currentEntityType)) {
    const defaultEntityType = selectedWorkspaceType?.data?.entity?.[0] ?? '';
    questionnaireForm
      .get('workspace_type_entity')
      ?.setValue(
        defaultEntityType
          ? { id: defaultEntityType, label: defaultEntityType }
          : '',
        {
          emitEvent: false,
        },
      );
  }

  const workspaceEntityTypes = (selectedWorkspaceType?.data?.entity ?? []).map(
    (item: string) => ({
      id: item,
      label: item,
    }),
  );

  return { workspaceEntityTypes };
}

export function isDuplicateFileError(error: string): boolean {
  return error?.includes(duplicateFileError());
}
